import { useQuery } from '@tanstack/react-query';
import { ChevronsUpDown, Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { searchClientes, TIPO_PF, type ClienteResumo } from '@/api/clientes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useDebounce } from '@/hooks/useDebounce';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { cn } from '@/lib/utils';

interface ClienteComboboxProps {
  value: string | null;
  onChange: (id: string | null) => void;
  className?: string;
}

/**
 * Lista sem portal: `absolute` por cima do conteúdo — mas DENTRO de um modal
 * vai para o fluxo, porque o modal tem teto de altura com rolagem e cortaria
 * a lista na borda. No fluxo, ela empurra o rodapé e o modal cresce (ou rola).
 */
const LIST_POSITION = 'absolute z-50 [[role=dialog]_&]:relative';

export function ClienteCombobox({ value, onChange, className }: ClienteComboboxProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Buscar clientes quando o termo muda
  const { data: results, isLoading } = useQuery<ClienteResumo[]>({
    queryKey: [...queryKeys.clientes.search, debouncedSearch],
    queryFn: () => searchClientes(debouncedSearch),
    enabled: open && debouncedSearch.length >= 2,
  });

  // Fechar ao clicar fora
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Label do selecionado
  const selectedLabel = results?.find((c) => c.id === value)?.nome;

  function handleSelect(cliente: ClienteResumo) {
    onChange(cliente.id);
    setSearchTerm(cliente.nome);
    setOpen(false);
  }

  function handleClear() {
    onChange(null);
    setSearchTerm('');
    setOpen(false);
  }

  return (
    <div ref={containerRef} className={cn('relative', className)} data-dropdown-open={open ? '' : undefined}>
      {/* Trigger / Input */}
      {!open && value && selectedLabel ? (
        <Button
          type="button"
          variant="outline"
          className="w-full justify-between font-normal"
          onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        >
          <span className="truncate">{selectedLabel}</span>
          <div className="flex items-center gap-1">
            <X
              className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground"
              onClick={(e) => { e.stopPropagation(); handleClear(); }}
            />
            <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
        </Button>
      ) : (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Buscar cliente..."
            data-no-dirty=""
            className="pl-9"
          />
        </div>
      )}

      {/* Dropdown */}
      {open && (
        <div className={cn(LIST_POSITION, 'mt-1 w-full rounded-md border border-border bg-popover shadow-md')}>
          {debouncedSearch.length < 2 ? (
            <p className="p-3 text-center text-xs text-muted-foreground">
              Digite ao menos 2 caracteres para buscar...
            </p>
          ) : isLoading ? (
            <div className="flex justify-center p-3">
              <Spinner size={16} label="Buscando..." />
            </div>
          ) : !results?.length ? (
            <p className="p-3 text-center text-xs text-muted-foreground">
              Nenhum cliente encontrado.
            </p>
          ) : (
            <ul className="max-h-48 overflow-y-auto py-1">
              {results.map((c) => (
                <li
                  key={c.id}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-accent',
                    c.id === value && 'bg-accent',
                  )}
                  onClick={() => handleSelect(c)}
                >
                  <div className="min-w-0 flex-1">
                    <p className="break-words font-medium">{c.nome}</p>
                    <p className="text-xs text-muted-foreground">
                      {c.tipo_pessoa === TIPO_PF ? 'PF' : 'PJ'} - {c.cpf_ou_cnpj || 'Sem documento'}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
